import type { Room } from '@/entities/room/model/types';
import type { Service } from '@/entities/service/model/types';
import type { EstimateItem } from '../model/types';
import { parseSafeNumber } from '@/shared/lib/number';

export interface SyncStats {
    /** Line items whose price changed (unaffected by priceIsManual). Meaning is load-bearing —
     *  callers/tests rely on `count` staying "price-only"; do not repurpose it. */
    count: number;
    diff: number;
    /** Line items whose name and/or nameKey changed (one axis, counted once even if both differ). */
    nameChanged: number;
    /** Line items whose categoryId changed. */
    categoryChanged: number;
    /** Line items whose unitType (or its unit fallback) changed. */
    unitChanged: number;
}

export interface SyncResult {
    updatedRooms: Room[];
    stats: SyncStats;
}

/**
 * Single answer to "did anything change" across every SyncStats axis. Used by both the
 * useProjectStore.syncPrices caller (gates the sync enqueue) and EditorPage's toast (gates the
 * "prices already up to date" message) — one predicate, two call sites, so a future axis added to
 * SyncStats can never be forgotten from one of two hand-written OR clauses.
 */
export const hasAnySyncChange = (stats: SyncStats): boolean =>
    stats.count > 0 || stats.nameChanged > 0 || stats.categoryChanged > 0 || stats.unitChanged > 0;

/**
 * Синхронизирует цены в проекте с актуальной базой данных.
 * @param {Room[]} rooms - Текущий список комнат.
 * @param {Service[] | Record<string, Service[]>} servicesDb - База данных услуг.
 */
export const syncProjectPrices = (
    rooms: Room[], 
    servicesDb: Service[] | Record<string, Service[]>
): SyncResult => {
    let updatedCount = 0;
    let priceDiff = 0;
    let nameChangedCount = 0;
    let categoryChangedCount = 0;
    let unitChangedCount = 0;

    const servicesMap = new Map<string, Service>();
    
    const servicesList = Array.isArray(servicesDb) 
        ? servicesDb 
        : Object.values(servicesDb).flat();

    servicesList.forEach(s => {
        if (s && s.id) {
            servicesMap.set(s.id, s);
        }
    });

    const updatedRooms = rooms.map(room => {
        let roomChanged = false;
        
        const newItems = room.items.map(item => {
            const currentService = servicesMap.get(item.serviceId);
            if (!currentService) return item;

            const currentPrice = parseSafeNumber(currentService.priceLabor, 0);
            const itemPrice = parseSafeNumber(item.priceLabor, 0);
            let changed = false;

            const updatedItem: EstimateItem = { ...item };
            // Per-item guard so a simultaneous name+nameKey mismatch counts as one nameChanged,
            // not two (the name axis is one thing, even though it spans two fields).
            let nameAxisChanged = false;

            // Sync name/nameKey from catalog unless the user has manually renamed this item.
            // nameIsManual=true protects the user's rename across catalog sync cycles.
            // Undefined (old data) is treated as false — overwrites until the persist migrate
            // callback (migrateProjectStore, SAFE-05) backfills it on the store's next rehydrate.
            if (currentService.name !== item.name && !item.nameIsManual) {
                updatedItem.name = currentService.name;
                changed = true;
                nameAxisChanged = true;
            }
            if (currentService.nameKey !== item.nameKey && !item.nameIsManual) {
                updatedItem.nameKey = currentService.nameKey;
                changed = true;
                nameAxisChanged = true;
            }
            if (nameAxisChanged) {
                nameChangedCount++;
            }
            if (currentService.categoryId !== item.categoryId) {
                updatedItem.categoryId = currentService.categoryId;
                updatedItem.category = currentService.categoryId;
                changed = true;
                categoryChangedCount++;
            }
            if ((currentService.unitType || currentService.unit) !== item.unitType) {
                updatedItem.unit = currentService.unit;
                updatedItem.unitType = currentService.unitType || currentService.unit;
                changed = true;
                unitChangedCount++;
            }

            // Only sync price if not manually overridden
            if (currentPrice !== itemPrice && !item.priceIsManual) {
                updatedCount++;
                priceDiff += (currentPrice - itemPrice) * (item.qty || 0);
                updatedItem.priceLabor = currentPrice;
                changed = true;
            }

            if (changed) {
                roomChanged = true;
                return updatedItem;
            }
            return item;
        });

        return roomChanged ? { ...room, items: newItems } : room;
    });

    return {
        updatedRooms,
        stats: {
            count: updatedCount,
            diff: priceDiff,
            nameChanged: nameChangedCount,
            categoryChanged: categoryChangedCount,
            unitChanged: unitChangedCount,
        }
    };
};
